import React, { useState, useEffect } from "react";
import './Style/common.css';
import "./Style/header.css";
import "./Style/SkillBuildingGames.css";
import "./Style/trophy_club.css";
import Header from "../../Common/Header";
import Section from "../../Common/Section";
import Loading from "../../Common/Loading";
import skillbg from './Image/skill_building_bg.png';

const SkillBuildingGames: React.FC = () => {
  type Game = {
    lessoncode?: string | null;
    dicfilename?: string | null;
    childshortdesc?: string | null;
    imgsrc?: string | null;
    besttime?: string | null;
  };

  type GameData = {
    games: Game[];
    student: {
      login?: string;
    };
    school?: {
      country?: string | null;
    };
  };

  const emptyData: GameData = { games: [], student: {}, school: {} };

  const [gameData, setGameData] = useState<GameData>(emptyData);
  const [loading, setLoading] = useState<boolean>(true);
  const [navLoading, setNavLoading] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false); // drives scale animation
  const apiUrl = process.env.REACT_APP_DOCUMENT_LINK;

  const subject = sessionStorage.getItem("Subject");
  const country = gameData.school?.country;
  const isNorthAmerica = country === "Canada" || country === "USA";

  // Same text rules as VB
  const gameMessage = isNorthAmerica
    ? "Games are added as you learn. These games reinforce skills learned."
    : "Games are added as you learn. These games reinforce skills learnt.";

  const subjectLabel =
    subject === "Mathematics" ? (isNorthAmerica ? "MATH" : "MATHS") : "English";

  useEffect(() => {
    const fetchGameScores = async () => {
      try {
        setLoading(true);
        setExpanded(false);
        const response = await fetch(`${apiUrl}skill_building_games/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            SchoolCode: sessionStorage.getItem("SchoolCode"),
            FamilyCode: sessionStorage.getItem("FamilyCode"),
            Login: sessionStorage.getItem("Login"),
            Subject: sessionStorage.getItem("Subject"),
          }),
        });
        const data = await response.json();
        if (response.ok && data && Array.isArray(data.games)) {
          setGameData({
            games: data.games,
            student: data.student || {},
            school: data.school || {},
          });
        } else {
          setGameData(emptyData);
        }
      } catch (error) {
        console.error('Error fetching game scores:', error);
        setGameData(emptyData);
      } finally {
        setLoading(false);
      }
    };
    fetchGameScores();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Trigger scale 0 -> 1 after cards have rendered once at scale(0)
  useEffect(() => {
    if (!loading && gameData.games.length > 0) {
      const t = setTimeout(() => setExpanded(true), 50);
      return () => clearTimeout(t);
    }
  }, [loading, gameData.games.length]);

  const handlePlayClick = (game: Game) => {
    if (!game.dicfilename || !game.lessoncode) return;

    setNavLoading(true);

    const folder = encodeURIComponent(game.dicfilename);
    const LsnTestCode = encodeURIComponent(game.lessoncode || "");
    const calledFrom = encodeURIComponent("/STUDENT/MSR/SkillBuildingGames");

    setTimeout(() => {
      window.location.href = `/STUDENT/Tutorials/Game?folder=${folder}&LsnTestCode=${LsnTestCode}&calledFrom=${calledFrom}`;
    }, 200);
  };

  const visibleGames = gameData.games.filter((game) => game.imgsrc && game.childshortdesc);

  return (
    <form>
      {loading && <Loading />}
      {navLoading && <Loading />}

      <div className="back-to-bottom">Back to Bottom</div>

      <div id="main_top_container">
        <div id="header">
          <Header />
        </div>
        <Section />
        <div id="msr_sbg_banner" className="subject_banner">
          <div id="msr_sbg_banner_text_img"></div>
        </div>
      </div>

      <div id="msr_sbg_page_btn_container">
        <div id="msr_sbg_page_btn_purchase" className="msr_sbg_page_btn msr_sbg_page_btn_last">
          <a>{subjectLabel}</a>
        </div>
      </div>

      <div
        className="contents msr_sbg_page_bg"
        style={{
          backgroundImage: `url(${skillbg})`,
          backgroundSize: "contain",
          minHeight: "100vh",
        }}
      >
        <div className="section_div">
          {/* id="wiz_game_message" is needed: CSS hides .subject_container_title but shows this id (same as VB) */}
          <div id="wiz_game_message" className="subject_container_title subject_container_title_sbg">
            <p>{gameMessage}</p>
          </div>

          {!loading && visibleGames.length === 0 && (
            <div className="no_games_message" style={{ textAlign: "center", marginTop: "20px", fontSize: "18px", color: "#f1f1f1ff" }}>
              No games available at the moment.
            </div>
          )}

          {visibleGames.length > 0 && (
            <div
              style={{
                display: "flex",
                flexWrap: "wrap",
                justifyContent: "center",
                gap: "20px",
                marginTop: "20px",
              }}
            >
              {visibleGames.map((game, index) => (
                <div
                  key={`${game.dicfilename}-${game.lessoncode}-${index}`}
                  id={`msr_sbg_single_box_${index}`}
                  className="divGameBlock"
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    backgroundColor: "#b3cae0",
                    padding: "2px",
                    width: "300px",
                    // ---- inline scaling animation ----
                    transform: expanded ? "scale(1)" : "scale(0)",
                    opacity: expanded ? 1 : 0,
                    transition: "transform 500ms linear, opacity 500ms linear",
                    transitionDelay: `${Math.min(index, 10) * 60}ms`,
                    willChange: "transform",
                  }}
                >
                  <p className="msr_sbg_single_box_bottom_title">{game.childshortdesc}</p>
                  <p className="msr_sbg_single_box_bottom_pb">{game.dicfilename || ""}</p>
                  <div
                    id={`msr_sbg_single_box_image_${index}`}
                    className="msr_sbg_single_box_image"
                    style={{
                      display: "block",
                      backgroundImage: `url(${game.imgsrc})`,
                      backgroundSize: "contain",
                      backgroundPosition: "center",
                      height: "150px",
                      borderRadius: "12px",
                    }}
                  ></div>
                  <div className="msr_sbg_single_box_bottom_container">
                    <div
                      className="play_game_btn"
                      style={{ cursor: "pointer" }}
                      onClick={() => handlePlayClick(game)}
                    >
                      <p>PLAY</p>
                    </div>

                    <div className="leaderboard_row_container">
                      <span className="msr_sbg_single_box_bottom_lb">
                        <p>Leaderboard</p>
                      </span>
                      <span className="month_btn btn_selected_bgcolor">
                        <p>This Month</p>
                      </span>
                      <span className="alltime_btn btn_unselected_bgcolor">
                        <p>All Time</p>
                      </span>
                      <div>
                        <div className="leaderboard_row">
                          <span className="leaderboard_icon leaderboard_icon_gold"></span>
                          <span className="leaderboard_score"></span>
                          <span className="leaderboard_name">
                            <p>{gameData.student.login || "Player"}</p>
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </form>
  );
};

export default SkillBuildingGames;